/**
 * MerchantBannerWizard — self-serve hero banner A/B wizard (MVP, B2).
 *
 * Linear merchant flow, no selectors ever shown:
 *   intro → detectar (popup onto the real store, MerchantBannerOverlay) →
 *   subir imágenes (staging assets) → previsualizar → resumen.
 *
 * Talks to the store popup over the TNX_MB_* protocol (merchantBannerProtocol),
 * origin-checked both ways. Experiment creation is B6 — this shell assembles
 * the exact variant config (buildSelfServeBannerConfig) and stops at the recap.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Button } from '@nimbus-ds/components'
import { authService } from '@/api/auth'
import { storeService } from '@/api/store'
import { uploadStagingAsset, getStagingPreviewUrl } from '@/api/assets'
import { experimentService } from '@/api/experiments'
import { createCustomEvent } from '@/api/customEvents'
import { trackAppEvent } from '@/lib/appTelemetry'
import {
  MB_MSG,
  MB_CMD,
  buildSelfServeBannerConfig,
  type MerchantBannerControl,
  type MerchantBannerUnsupportedReason,
  type MbUploads,
} from '@/lib/merchantBannerProtocol'

type Step = 'intro' | 'detect' | 'upload' | 'preview' | 'recap' | 'done'

/** Max experiment duration — the auto-stop backstop (spec §3.4). Assignment
 *  stops at end_date_time immediately; the aggregation cron completes it. */
const MAX_DURATION_DAYS = 30

interface UploadSlot {
  uploading?: boolean
  error?: string
  asset_id?: string
  s3_key?: string
  previewUrl?: string
}

const STEP_TITLES: Record<Step, string> = {
  intro: 'Probá otro banner',
  detect: 'Encontrá tu banner',
  upload: 'Subí el banner nuevo',
  preview: 'Miralo en tu tienda',
  recap: 'Todo listo',
  done: 'Prueba en marcha',
}

const UNSUPPORTED_COPY: Record<MerchantBannerUnsupportedReason, string> = {
  carousel:
    'Tu tienda usa un carrusel de banners y todavía no está soportado en esta versión. Te vamos a avisar cuando lo esté.',
  not_found:
    'No encontramos un banner en tu página de inicio. Si creés que es un error, escribinos.',
  wrong_page: 'La ventana de tu tienda no está en la página de inicio.',
}

const MAX_SIZE_KB = 500
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export function MerchantBannerWizard() {
  const storeId = authService.getCurrentStoreId()
  const uploadSessionId = useMemo(() => crypto.randomUUID(), [])

  const [step, setStep] = useState<Step>('intro')
  const [storeUrl, setStoreUrl] = useState<string | null>(null)
  const [storeError, setStoreError] = useState<string | null>(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const [overlayStatus, setOverlayStatus] = useState<'waiting' | 'proposed' | 'confirmed'>('waiting')
  const [unsupported, setUnsupported] = useState<MerchantBannerUnsupportedReason | null>(null)
  const [control, setControl] = useState<MerchantBannerControl | null>(null)
  const [uploads, setUploads] = useState<{ mobile: UploadSlot; desktop: UploadSlot }>({
    mobile: {},
    desktop: {},
  })
  const [previewingVariant, setPreviewingVariant] = useState(false)

  const popupRef = useRef<Window | null>(null)
  const controlRef = useRef<MerchantBannerControl | null>(null)
  controlRef.current = control

  // Pilot instrumentation (B10): started on mount; abandoned-with-step on
  // unmount unless the launch completed.
  const stepRef = useRef<Step>(step)
  stepRef.current = step
  useEffect(() => {
    trackAppEvent('wizard_started')
    return () => {
      if (stepRef.current !== 'done') {
        trackAppEvent('wizard_abandoned', { step: stepRef.current })
      }
    }
  }, [])

  const storeOrigin = useMemo(() => {
    if (!storeUrl) return null
    try {
      return new URL(storeUrl).origin
    } catch {
      return null
    }
  }, [storeUrl])

  // --- Resolve store URL (same fallbacks as the consultant wizard: JWT fields
  // first, store API for Google-SSO tokens that don't carry the domain).
  useEffect(() => {
    let cancelled = false
    const resolve = async () => {
      const user = authService.getUserInfo() as any
      let url: string | undefined = user?.store_url || user?.original_domain
      if (!url && storeId) {
        try {
          const info = await storeService.getStore(storeId)
          url = (info as any)?.domain
        } catch {
          /* handled below */
        }
      }
      if (cancelled) return
      if (!url) {
        setStoreError('No pudimos encontrar la dirección de tu tienda. Probá volver a iniciar sesión.')
        return
      }
      setStoreUrl(`https://${url.replace(/^https?:\/\//, '')}`)
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [storeId])

  const sendToPopup = useCallback(
    (type: string, payload?: Record<string, unknown>) => {
      if (popupRef.current && !popupRef.current.closed && storeOrigin) {
        popupRef.current.postMessage({ type, ...(payload || {}) }, storeOrigin)
      }
    },
    [storeOrigin]
  )

  // --- Overlay message listener (origin-checked against the store).
  useEffect(() => {
    if (!storeOrigin) return
    const handler = (event: MessageEvent) => {
      if (event.origin !== storeOrigin) return
      const type = event.data?.type
      if (typeof type !== 'string' || !type.startsWith('TNX_MB_')) return
      switch (type) {
        case MB_MSG.READY:
          sendToPopup(MB_CMD.ACK)
          // Popup reopened mid-flow: re-adopt the confirmed control silently,
          // and replay a preview the merchant asked for while it was closed.
          if (controlRef.current) {
            sendToPopup(MB_CMD.ADOPT, { selector: controlRef.current.selector })
            if (pendingPreviewRef.current && previewUrlRef.current) {
              sendToPopup(MB_CMD.PREVIEW, { previewUrl: previewUrlRef.current })
              pendingPreviewRef.current = false
              setPreviewingVariant(true)
            }
          }
          break
        case MB_MSG.CANDIDATE:
          setOverlayStatus('proposed')
          setUnsupported(null)
          break
        case MB_MSG.CONFIRMED: {
          const c = event.data?.control as MerchantBannerControl | undefined
          if (c?.selector && c?.elementType) {
            setControl(c)
            setOverlayStatus('confirmed')
            setUnsupported(null)
          }
          break
        }
        case MB_MSG.UNSUPPORTED: {
          const reason = event.data?.reason as MerchantBannerUnsupportedReason
          if (reason === 'wrong_page') break // overlay guides the merchant back home
          setUnsupported(reason || 'not_found')
          break
        }
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [storeOrigin, sendToPopup])

  // --- Popup lifecycle watchdog.
  useEffect(() => {
    const interval = setInterval(() => {
      setPopupOpen(!!popupRef.current && !popupRef.current.closed)
    }, 800)
    return () => clearInterval(interval)
  }, [])

  const openStore = useCallback(() => {
    if (!storeUrl) return
    const sep = storeUrl.includes('?') ? '&' : '?'
    const url =
      `${storeUrl}${sep}tnx_visual_editor=true&tnx_merchant_banner=1` +
      `&tnx_app_origin=${encodeURIComponent(window.location.origin)}`
    popupRef.current = window.open(url, 'TNXMerchantBanner', 'width=1280,height=850')
    setPopupOpen(true)
    setOverlayStatus(controlRef.current ? 'confirmed' : 'waiting')
  }, [storeUrl])

  // --- Uploads (same constraints as the consultant template: type + 500 KB).
  const handleFile = useCallback(
    async (slot: 'mobile' | 'desktop', file: File) => {
      const setSlot = (patch: UploadSlot) =>
        setUploads((prev) => ({ ...prev, [slot]: { ...prev[slot], ...patch } }))
      if (!ALLOWED_TYPES.includes(file.type)) {
        setSlot({ error: 'Formato no soportado. Usá JPG, PNG o WebP.' })
        return
      }
      if (file.size > MAX_SIZE_KB * 1024) {
        setSlot({ error: `La imagen pesa ${(file.size / 1024).toFixed(0)} KB. El máximo es ${MAX_SIZE_KB} KB.` })
        return
      }
      if (!storeId) return
      setSlot({ uploading: true, error: undefined })
      try {
        const imageType = slot === 'mobile' ? 'mobile_image' : 'desktop_image'
        const result = await uploadStagingAsset(storeId, uploadSessionId, file, imageType)
        setSlot({
          uploading: false,
          asset_id: result.asset_id,
          s3_key: result.s3_key,
          previewUrl: getStagingPreviewUrl(result.s3_key),
        })
      } catch (err: any) {
        setSlot({ uploading: false, error: err?.message || 'No pudimos subir la imagen. Probá de nuevo.' })
      }
    },
    [storeId, uploadSessionId]
  )

  const previewUrlForStore = uploads.desktop.previewUrl || uploads.mobile.previewUrl
  const previewUrlRef = useRef<string | undefined>(undefined)
  previewUrlRef.current = previewUrlForStore
  const pendingPreviewRef = useRef(false)

  const showVariant = () => {
    if (!previewUrlForStore) return
    if (!popupRef.current || popupRef.current.closed) {
      // Reopen and queue: on READY the handler re-adopts the control and
      // replays this preview — no second click needed.
      pendingPreviewRef.current = true
      openStore()
      return
    }
    sendToPopup(MB_CMD.PREVIEW, { previewUrl: previewUrlForStore })
    setPreviewingVariant(true)
    popupRef.current?.focus()
  }

  const showControl = () => {
    sendToPopup(MB_CMD.REVERT)
    setPreviewingVariant(false)
    popupRef.current?.focus()
  }

  // --- B6: launch (experiment + CTR custom events + Seguro + auto-stop). ---
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)

  const doLaunch = async () => {
    const cfg = assembledConfig
    if (!control || !cfg || !storeId || launching) return
    setLaunching(true)
    setLaunchError(null)
    try {
      // Unique per launch: event_id is evt-<name>-<store>, so a second banner
      // test on the same store must not collide with an active earlier one.
      const suffix = Date.now().toString(36)
      const clickEventName = `banner_test_click_${suffix}`
      const seenEventName = `banner_test_seen_${suffix}`

      const payload: any = {
        name: `Banner principal — prueba A/B (${new Date().toLocaleDateString('es-AR')})`,
        hypothesis: 'Un banner nuevo genera más clics que el actual.',
        objective: 'Aumentar los clics en el banner principal',
        type: 'tier1_visual',
        tier: 1,
        is_negative: false,
        mde: null,
        primary_segment: 'overall',
        // Self-serve is Seguro locked (spec §1.1) — never the store default.
        risk_level: 'safe',
        hard_to_revert: false,
        traffic_split: { control: 50, treatment: 50 },
        targeting: {
          // COMMA-SEPARATED STRING, not an array — tnx-core and the bundle
          // template both .trim()/.split(',') this field; an array threw in
          // both and killed experiment application store-wide (aug-6 alarm).
          url_patterns: '/',
          device_conditions: { desktop: true, mobile: true, tablet: true },
          exclusions: [],
        },
        variants: {
          control: { name: 'Control', description: 'Banner actual', config: {} },
          treatment: {
            name: 'Banner nuevo',
            description: 'Banner alternativo',
            config: {
              type: 'tier1_visual',
              modifications: [],
              captured_events: [],
              elements_to_hide: cfg.elements_to_hide,
              elements_to_insert: cfg.elements_to_insert,
              ...(cfg.elements_to_swap.length ? { elements_to_swap: cfg.elements_to_swap } : {}),
              // Self-serve marker: filters the merchant results view (B7) and
              // feeds the drift guard (B8 — banner-drift.js reads control_image).
              self_serve_banner: {
                selector: control.selector,
                element_type: control.elementType,
                control_image: control.imageUrl,
                control_size: { width: control.width, height: control.height },
              },
            },
          },
        },
        funnel_config: { use_default: true, selected_steps: [], custom_steps: [] },
        // Primary = banner clicks per session (BB+ROPE verdict runs on
        // goal_conversions/sessions). Visibility rides as secondary for the
        // exposure sanity check; purchases stay visible as guardrail.
        goals: {
          primary: { type: 'custom_event', event_name: clickEventName, label: 'Clic en el banner' },
          secondary: [
            { type: 'custom_event', event_name: seenEventName, label: 'Banner visto' },
            { type: 'purchase', label: 'Compra' },
          ],
        },
        timing: {
          end_date_time: new Date(Date.now() + MAX_DURATION_DAYS * 86400_000).toISOString(),
        },
      }

      const created = await experimentService.createExperiment(payload)

      // Events go DIRECT to /definitions (status active — the captured_events
      // path would create inert drafts the tracker never loads).
      await createCustomEvent({
        store_id: storeId,
        event_name: clickEventName,
        display_name: 'Clic en banner (prueba A/B)',
        category: 'interaction',
        tracking_config: {
          mode: 'auto',
          auto_config: {
            interaction_type: 'click',
            click_config: { selector: control.selector, capture_text: false },
          },
        },
        experiment_id: created.id,
        tags: ['self_serve_banner'],
      })
      await createCustomEvent({
        store_id: storeId,
        event_name: seenEventName,
        display_name: 'Banner visto (prueba A/B)',
        category: 'engagement',
        tracking_config: {
          mode: 'auto',
          auto_config: {
            interaction_type: 'visibility',
            visibility_config: { selector: control.selector, threshold: 0.5, duration_ms: 1000 },
          },
        },
        experiment_id: created.id,
        tags: ['self_serve_banner'],
      })

      // Activate only after both events exist — a failure above leaves an
      // inactive draft experiment, never a live test measuring nothing.
      await experimentService.updateExperiment(created.id, { status: 'active' } as any)
      trackAppEvent('launched', { experiment_id: created.id })
      trackAppEvent('wizard_completed')
      setStep('done')
    } catch (err: any) {
      setLaunchError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'No pudimos iniciar la prueba. Probá de nuevo en unos minutos.'
      )
    } finally {
      setLaunching(false)
    }
  }

  const assembledConfig = useMemo(() => {
    if (!control || !uploads.mobile.asset_id) return null
    const mbUploads: MbUploads = {}
    if (uploads.mobile.asset_id) {
      mbUploads.mobile = {
        asset_id: uploads.mobile.asset_id,
        s3_key: uploads.mobile.s3_key!,
        previewUrl: uploads.mobile.previewUrl!,
      }
    }
    if (uploads.desktop.asset_id) {
      mbUploads.desktop = {
        asset_id: uploads.desktop.asset_id,
        s3_key: uploads.desktop.s3_key!,
        previewUrl: uploads.desktop.previewUrl!,
      }
    }
    return buildSelfServeBannerConfig(control, mbUploads)
  }, [control, uploads])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!storeId) {
    return (
      <Card>
        <Card.Body>
          <Text className="text-center">No pudimos identificar tu tienda. Volvé a iniciar sesión.</Text>
        </Card.Body>
      </Card>
    )
  }

  const steps: Step[] = ['intro', 'detect', 'upload', 'preview', 'recap']
  const stepIndex = steps.indexOf(step)

  return (
    <div className="space-y-4">
      <div>
        <Link to="/merchant" className="text-sm text-muted-foreground hover:text-foreground">
          ← Volver
        </Link>
        <div className="mt-2 flex items-baseline justify-between">
          <Text fontWeight="bold" fontSize="highlight" color="neutral-textHigh">
            {STEP_TITLES[step]}
          </Text>
          {step !== 'intro' && step !== 'done' && (
            <Text fontSize="caption">Paso {stepIndex} de {steps.length - 1}</Text>
          )}
        </div>
      </div>

      {step === 'intro' && (
        <Card>
          <Card.Body>
            <div className="space-y-3">
              <Text>
                Vas a probar un banner nuevo contra el actual. La mitad de tus visitas ve cada uno, y
                medimos cuál genera más clics.
              </Text>
              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Confirmá cuál es tu banner actual (se abre tu tienda).</li>
                <li>Subí el banner nuevo (versión celular y computadora).</li>
                <li>Miralo en tu tienda antes de decidir.</li>
              </ul>
              <Button appearance="primary" onClick={() => setStep('detect')}>
                Empezar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 'detect' && (
        <Card>
          <Card.Body>
            <div className="space-y-3">
              {unsupported ? (
                <Text>{UNSUPPORTED_COPY[unsupported]}</Text>
              ) : control ? (
                <>
                  <Text>✓ Banner confirmado.</Text>
                  {control.imageUrl ? (
                    <img
                      src={control.imageUrl}
                      alt="Tu banner actual"
                      className="max-h-40 w-full rounded border object-cover"
                    />
                  ) : (
                    <div className="flex h-24 items-center justify-center rounded border bg-slate-50 text-xs text-slate-400">
                      Vista previa no disponible — está marcado en tu tienda
                    </div>
                  )}
                  <Text fontSize="caption">
                    Tamaño actual: {control.width}×{control.height} px
                  </Text>
                  <Button appearance="primary" onClick={() => setStep('upload')}>
                    Continuar
                  </Button>
                </>
              ) : (
                <>
                  <Text>
                    Se abre una ventana con tu tienda y te mostramos cuál creemos que es tu banner.
                    Confirmalo ahí y volvé a esta pestaña.
                  </Text>
                  <Button appearance="primary" onClick={openStore} disabled={!storeUrl}>
                    {popupOpen ? 'Volver a abrir tu tienda' : 'Abrir tu tienda'}
                  </Button>
                  {storeError && <Text fontSize="caption">{storeError}</Text>}
                  {popupOpen && overlayStatus !== 'confirmed' && (
                    <Text fontSize="caption">
                      {overlayStatus === 'proposed'
                        ? 'Confirmá el banner en la ventana de tu tienda…'
                        : 'Esperando la ventana de tu tienda…'}
                    </Text>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 'upload' && control && (
        <Card>
          <Card.Body>
            <div className="space-y-4">
              <Text>
                Subí la imagen nueva. Tu banner actual mide {control.width}×{control.height} px en
                computadora — usá una imagen del mismo tamaño para que no se deforme.
              </Text>
              {(['mobile', 'desktop'] as const).map((slot) => {
                const s = uploads[slot]
                return (
                  <div key={slot} className="space-y-1">
                    <Text fontWeight="medium">
                      {slot === 'mobile' ? 'Versión celular' : 'Versión computadora'}
                      {slot === 'mobile' && <span className="ml-1 text-xs text-orange-500">(obligatoria)</span>}
                    </Text>
                    {s.previewUrl ? (
                      <div className="space-y-1">
                        <img src={s.previewUrl} alt={`Banner ${slot}`} className="max-h-32 w-full rounded border object-cover" />
                        <button
                          className="text-xs text-muted-foreground underline"
                          onClick={() => setUploads((prev) => ({ ...prev, [slot]: {} }))}
                        >
                          Cambiar imagen
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`flex h-20 cursor-pointer items-center justify-center rounded border-2 border-dashed text-sm ${
                          s.error ? 'border-red-300 bg-red-50 text-red-600' : 'border-slate-300 bg-slate-50 text-slate-500'
                        }`}
                      >
                        <input
                          type="file"
                          accept={ALLOWED_TYPES.join(',')}
                          className="hidden"
                          disabled={s.uploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0]
                            if (f) handleFile(slot, f)
                          }}
                        />
                        {s.uploading ? 'Subiendo…' : s.error || 'Tocá para elegir la imagen'}
                      </label>
                    )}
                  </div>
                )
              })}
              <Text fontSize="caption">JPG, PNG o WebP · máximo {MAX_SIZE_KB} KB</Text>
              <Button
                appearance="primary"
                disabled={!uploads.mobile.asset_id}
                onClick={() => setStep('preview')}
              >
                Continuar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 'preview' && (
        <Card>
          <Card.Body>
            <div className="space-y-3">
              <Text>Miralo puesto en tu tienda antes de seguir.</Text>
              <div className="flex gap-2">
                <Button appearance={previewingVariant ? 'neutral' : 'primary'} onClick={showVariant}>
                  Ver banner nuevo
                </Button>
                <Button appearance="neutral" onClick={showControl} disabled={!previewingVariant}>
                  Ver banner actual
                </Button>
              </div>
              {!popupOpen && (
                <Text fontSize="caption">
                  La ventana de tu tienda está cerrada — al tocar "Ver banner nuevo" se vuelve a abrir.
                </Text>
              )}
              <Button appearance="primary" onClick={() => { showControl(); setStep('recap') }}>
                Continuar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 'recap' && control && (
        <Card>
          <Card.Body>
            <div className="space-y-3">
              <Text>
                Vamos a mostrar cada banner a la mitad de tus visitas y medir cuál genera más clics.
                Vos no tenés que hacer nada más: te avisamos cuando haya un resultado.
              </Text>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Text fontSize="caption">Banner actual</Text>
                  {control.imageUrl ? (
                    <img src={control.imageUrl} alt="Actual" className="h-20 w-full rounded border object-cover" />
                  ) : (
                    <div className="flex h-20 items-center justify-center rounded border bg-slate-50 text-[10px] text-slate-400">
                      Sin vista previa
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Text fontSize="caption">Banner nuevo</Text>
                  {previewUrlForStore && (
                    <img src={previewUrlForStore} alt="Nuevo" className="h-20 w-full rounded border object-cover" />
                  )}
                </div>
              </div>
              <Button appearance="primary" onClick={doLaunch} disabled={launching || !assembledConfig}>
                {launching ? 'Iniciando…' : 'Iniciar la prueba'}
              </Button>
              {launchError && <Text fontSize="caption">{launchError}</Text>}
              <Text fontSize="caption">
                La prueba corre sola y se detiene automáticamente (máximo {MAX_DURATION_DAYS} días).
              </Text>
              {assembledConfig && (
                <details className="text-xs text-muted-foreground">
                  <summary>Detalle técnico</summary>
                  <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-2">
                    {JSON.stringify(assembledConfig, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 'done' && (
        <Card>
          <Card.Body>
            <div className="space-y-3 text-center">
              <span className="text-4xl">🚀</span>
              <Text fontWeight="medium" color="neutral-textHigh">
                ¡Tu prueba está en marcha!
              </Text>
              <Text>
                Desde ahora, la mitad de tus visitas ve el banner nuevo y la otra mitad el actual.
                No tenés que hacer nada más: la prueba se detiene sola cuando haya un resultado (o
                a los {MAX_DURATION_DAYS} días como máximo).
              </Text>
              <Text fontSize="caption">
                Importante: no cambies el banner desde el administrador de tu tienda mientras la
                prueba está activa.
              </Text>
              <Link to="/merchant">
                <Button appearance="primary">Volver al inicio</Button>
              </Link>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
